'use client'

import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { twMerge } from '@/lib/classname/twMerge'
import { ProductType, type ProductPackaging } from '@gql-generated/graphql'
import type React from 'react'
import { useSiteConfig } from '../../../config/hooks/useSiteConfig.client'
import { getTierPriceForQuantity } from '../../services/product.filtering'
import type { ProductRowSource } from '../product-row/ProductRow.client'
import type { ProductPriceDiscountsItemDiscount } from './ProductPriceDiscounts.client'
import { ProductPriceDiscounts } from './ProductPriceDiscounts.client'
import { ProductPriceDisplay } from './ProductPriceDisplay.client'
import { ProductPriceHeader, type Discount } from './ProductPriceHeader.client'

export const sizes = ['xl', '2xl', '4xl'] as const

export type ProductPriceProps = {
  /** The price */
  price: number

  /** The unit price */
  unitPrice?: number | null

  priceQuantity?: number | null

  /** The price including tax */
  inclTaxPrice?: number | null

  /** The discounted infos */
  discount?: Discount

  cartItemDiscount?: ProductPriceDiscountsItemDiscount | null

  /** The product's type */
  type?: ProductType

  /** The product's packaging */
  packaging: ProductPackaging | null | undefined

  /** The size of the price */
  size: (typeof sizes)[number]

  /** Whether to display the unit */
  noUnits?: boolean

  /** Whether to keep the head space */
  keepHeadSpace?: boolean

  /** Additional classes to add to the component */
  className?: string

  /** Additional classes to add to the price wrapper */
  wrapperClassName?: string

  displaySavedAmount?: boolean

  selectedQuantity?: number

  /** The source context where this component is used */
  source?: ProductRowSource
}

function resolveQuantitySalesPrice({
  price,
  discount,
  hasDiscount,
  selectedQuantity,
}: StrictPick<ProductPriceProps, 'price' | 'discount' | 'selectedQuantity'> & {
  hasDiscount: boolean
}): { displayedPrice: number; effectiveDiscountPercentage: number } {
  const fallback = { displayedPrice: price, effectiveDiscountPercentage: discount?.percentage ?? 0 }

  if (typeof selectedQuantity !== 'number' || selectedQuantity <= 0) return fallback
  if (!discount?.tiersPrices?.length) return fallback

  const oldPrice = hasDiscount ? price + discount.price : undefined
  const tierPrice = getTierPriceForQuantity(discount.tiersPrices, selectedQuantity)

  if (typeof tierPrice !== 'number' || !oldPrice || oldPrice <= 0) return fallback

  return {
    displayedPrice: tierPrice,
    effectiveDiscountPercentage: ((oldPrice - tierPrice) / oldPrice) * 100,
  }
}

export function ProductPrice({
  price,
  unitPrice,
  priceQuantity,
  inclTaxPrice,
  discount,
  cartItemDiscount,
  type,
  packaging,
  size,
  noUnits = false,
  keepHeadSpace = false,
  className,
  wrapperClassName,
  displaySavedAmount = true,
  selectedQuantity = -1,
  source,
}: ProductPriceProps): React.ReactElement | null {
  const isAuthenticated = useIsAuthenticated()
  const { config } = useSiteConfig()

  const showPricesAndEcoPart = !!config?.catalog?.isPublicPricesEnabled || isAuthenticated

  if (price === 0 || !showPricesAndEcoPart) return null

  const hasUnitPrice = !!unitPrice && unitPrice > 0
  const isProduct = type === ProductType.Product
  const hasDiscount = !!discount?.percentage && !!discount?.price

  const hasConditioningLayout = !!priceQuantity && hasUnitPrice

  const wrapperClass = twMerge(
    'inline-flex items-baseline gap-1 text-nowrap',
    hasUnitPrice && 'flex-row',
    wrapperClassName,
    hasConditioningLayout && 'flex-col items-start',
  )

  const sizeMap: Record<(typeof sizes)[number], string> = {
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '4xl': 'text-4xl',
  }

  const priceClass = twMerge(
    sizeMap[size],
    'font-bold leading-compact text-brand-2-900',
    hasDiscount && 'text-strawberry-red-600',
  )

  const { displayedPrice, effectiveDiscountPercentage } = resolveQuantitySalesPrice({
    price,
    discount,
    hasDiscount,
    selectedQuantity,
  })

  const showPercentageTag = hasDiscount && Math.abs(effectiveDiscountPercentage) >= 2
  const updatedDiscount = hasDiscount
    ? {
        ...discount,
        percentage: effectiveDiscountPercentage,
      }
    : discount

  return (
    <div className={className} data-testid="product-price">
      <ProductPriceHeader
        keepHeadSpace={keepHeadSpace}
        isProduct={isProduct}
        hasDiscount={hasDiscount}
        price={displayedPrice}
        discount={updatedDiscount}
        showPercentageTag={showPercentageTag}
      />

      <ProductPriceDisplay
        price={displayedPrice}
        unitPrice={unitPrice}
        priceQuantity={priceQuantity}
        inclTaxPrice={inclTaxPrice}
        packaging={packaging}
        noUnits={noUnits}
        priceClass={priceClass}
        wrapperClass={wrapperClass}
      />

      <ProductPriceDiscounts
        displaySavedAmount={displaySavedAmount}
        cartItemDiscount={cartItemDiscount}
        hasDiscount={hasDiscount}
        source={source}
      />
    </div>
  )
}
